import React, { useState } from 'react'

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    website_url: '' // Honeypot field
  })
  const [status, setStatus] = useState({ type: '', message: '' })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setStatus({ type: '', message: '' })

    // Basic validation
    if (!formData.name || !formData.phone || !formData.email || !formData.message) {
      setStatus({ type: 'error', message: 'Udfyld venligst alle påkrævede felter.' })
      setIsSubmitting(false)
      return
    }

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formData)
      })

      if (response.ok) {
        setStatus({ 
          type: 'success', 
          message: 'Tak for din henvendelse! Vi vender tilbage til dig hurtigst muligt.' 
        })
        setFormData({
          name: '',
          phone: '',
          email: '',
          message: '',
          website_url: ''
        })
      } else {
        const errData = await response.json().catch(() => ({}))
        setStatus({ 
          type: 'error', 
          message: errData.message || 'Der skete en fejl under afsendelsen. Prøv venligst igen.' 
        })
      }
    } catch (error) {
      setStatus({ 
        type: 'error', 
        message: 'Der opstod en netværksfejl. Kontroller din internetforbindelse og prøv igen.' 
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-md border border-slate-100">
      <h3 className="text-xl sm:text-2xl font-bold text-brand-blue mb-2">Skriv til os</h3>
      <p className="text-sm text-slate-500 mb-6">Udfyld formularen herunder, så kontakter vi dig hurtigst muligt.</p>

      {status.message && (
        <div className={`p-4 rounded-xl mb-6 text-sm font-medium flex items-start gap-3 ${
          status.type === 'success' 
            ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' 
            : 'bg-rose-50 text-rose-800 border border-rose-200'
        }`}>
          <i className={`fa-solid ${status.type === 'success' ? 'fa-circle-check text-emerald-500' : 'fa-circle-exclamation text-rose-500'} mt-0.5 text-base`}></i>
          <div>{status.message}</div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Honeypot Field - Absolutely hidden from humans */}
        <div className="sr-only absolute -top-[9999px] -left-[9999px]" aria-hidden="true">
          <label htmlFor="website_url">Website URL</label>
          <input
            id="website_url"
            type="text"
            name="website_url"
            value={formData.website_url}
            onChange={handleChange}
            tabIndex="-1"
            autoComplete="off"
          />
        </div>

        {/* Name Field */}
        <div>
          <label htmlFor="name" className="block text-sm font-semibold text-slate-700 mb-1.5">
            Navn <span className="text-brand-orange">*</span>
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
              <i className="fa-solid fa-user text-sm" aria-hidden="true"></i>
            </div>
            <input
              id="name"
              type="text"
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
              className="block w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-orange/20 focus:border-brand-orange transition-all text-sm"
              placeholder="Dit fulde navn"
            />
          </div>
        </div>

        {/* Phone Field */}
        <div>
          <label htmlFor="phone" className="block text-sm font-semibold text-slate-700 mb-1.5">
            Telefonnummer <span className="text-brand-orange">*</span>
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
              <i className="fa-solid fa-phone text-sm" aria-hidden="true"></i>
            </div>
            <input
              id="phone"
              type="tel"
              name="phone"
              required
              value={formData.phone}
              onChange={handleChange}
              className="block w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-orange/20 focus:border-brand-orange transition-all text-sm"
              placeholder="Dit telefonnummer"
            />
          </div>
        </div>

        {/* Email Field */}
        <div>
          <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-1.5">
            E-mail <span className="text-brand-orange">*</span>
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
              <i className="fa-solid fa-envelope text-sm" aria-hidden="true"></i>
            </div>
            <input
              id="email"
              type="email"
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              className="block w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-orange/20 focus:border-brand-orange transition-all text-sm"
              placeholder="din@email.dk"
            />
          </div>
        </div>

        {/* Message Field */}
        <div>
          <label htmlFor="message" className="block text-sm font-semibold text-slate-700 mb-1.5">
            Beskrivelse <span className="text-brand-orange">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            required
            rows="4"
            value={formData.message}
            onChange={handleChange}
            className="block w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-orange/20 focus:border-brand-orange transition-all text-sm resize-none"
            placeholder="Skriv din besked eller beskrivelse af projektet her..."
          ></textarea>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full py-3.5 px-6 bg-brand-orange hover:bg-brand-orange-hover disabled:bg-slate-400 text-white font-semibold rounded-xl transition-all shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-brand-orange/20 flex items-center justify-center gap-2 cursor-pointer"
        >
          {isSubmitting ? (
            <>
              <i className="fa-solid fa-spinner animate-spin"></i>
              <span>Sender...</span>
            </>
          ) : (
            <>
              <i className="fa-solid fa-paper-plane"></i>
              <span>Send besked</span>
            </>
          )}
        </button>
      </form>
    </div>
  )
}
